import { inject, Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, TitleStrategy } from "@angular/router";
import { DASHBOARD_TYPE } from "@front/m19-injection-token";
import { AuthService } from "@front/m19-services";
import { AmcStatsComponent } from "@m19-board/amc/amc-stats.component";
import { AdminLoginComponent } from "@m19-board/auth/admin-login/admin-login.component";
import { LoginComponent } from "@m19-board/auth/login/login.component";
import { PasswordResetComponent } from "@m19-board/auth/password-reset/password-reset.component";
import { ProfileComponent } from "@m19-board/auth/profile-page/profile.component";
import { RequestPasswordResetComponent } from "@m19-board/auth/request-password-reset/request-password-reset.component";
import { TermsOfUseComponent } from "@m19-board/auth/terms-of-use/terms-of-use.component";
import { accountSelectionResolver } from "@m19-board/resolvers/account-selection-resolver/account-selection.resolver";
import { SbStrategyFormPageComponent } from "@m19-board/strategies/sb-strategy-form-page/sb-strategy-form-page.component";
import { M19Route } from "@m19-board/utils/M19Route";
import { map, switchMap } from "rxjs/operators";
import { ActivitiesComponent } from "./activities/activities.component";
import { AgencyBoardComponent } from "./agency-board/agency-board.component";
import { RegisterInviteComponent } from "./auth/register/register-invite.component";
import { RegisterComponent } from "./auth/register/register.component";
import { SearchTermRanksComponent } from "./brand-analytics/search-term-ranks/search-term-ranks.component";
import { BrandAliasComponent } from "./brand/brand-alias.component";
import { CatalogPageComponent } from "./catalog/catalog-page.component";
import { CatalogUploadComponent } from "./catalog/catalog-upload.component";
import { BoardLayoutComponent } from "./containers/board-layout/board-layout.component";
import { Dashboard360LayoutComponent } from "./containers/dashboard360-layout/dashboard360-layout.component";
import { OnboardingLayoutComponent } from "./containers/onboarding/onboarding-layout.component";
import { AdvertisingStatsComponent } from "./insights/advertising-stats/advertising-stats.component";
import { HourlyPageComponent } from "./insights/hourly-view/hourly-page/hourly-page.component";
import { InventoryComponent } from "./inventory/inventory.component";
import { KeywordTrackerDetailsComponent } from "./keyword-tracker/keyword-tracker-details/keyword-tracker-details.component";
import { KeywordTrackerPageComponent } from "./keyword-tracker/keyword-tracker-page.component";
import { BoardType, LayoutSelectorService } from "./layout-selector.service";
import { NotificationsCenterComponent } from "./notifications-center/notifications-center.component";
import { AccountLinkingPageComponent } from "./onboarding/account-linking-page.component";
import { FreeTestPageComponent } from "./onboarding/free-test-subscription-page.component";
import { OnboardingCallPageComponent } from "./onboarding/onboarding-call-page.component";
import { StatsDownloadingPageComponent } from "./onboarding/stats-downloading-page.component";
import { WhitelabelNoAccountPageComponent } from "./onboarding/whitelabel-noaccount-page.component";
import { StatsOverlayWrapperComponent } from "./overlay/stats-overlay-wrapper.component";
import { ProductAnalysisPageComponent } from "./product-analysis/product-analysis-page.component";
import { ProfitAndLossComponent } from "./profit-and-loss/profit-and-loss.component";
import { SegmentsComponent } from "./segments/segment-manager.component";
import { OnboardingService, OnBoardingStepRedirects } from "./services/onboarding.service";
import { AccountSettingComponent } from "./settings/account-settings/account-setting.component";
import { BillingSettingsComponent } from "./settings/billing-settings/billing-settings.component";
import { SelfServiceFreemiumComponent } from "./settings/billing-settings/selfservice-freemium/selfservice-freemium.component";
import { DataSharingComponent } from "./settings/data-sharing/data-sharing.component";
import { UserManagementComponent } from "./settings/user-management/user-management.component";
import { ShareOfVoiceComponent } from "./share-of-voice/share-of-voice.component";
import { SbManagerComponent } from "./sponsored-brand/sb-manager/sb-manager.component";
import { SbPageComponent } from "./sponsored-brand/sb-page/sb-page.component";
import { SdManagerComponent } from "./sponsored-display/sd-manager/sd-manager.component";
import { SdStrategyCreationPageComponent } from "./sponsored-display/sd-manager/sd-strategy-creation/sd-strategy-creation-page.component";
import { SdPageComponent } from "./sponsored-display/sd-page/sd-page.component";
import { SpStrategyGroupCreationPageComponent } from "./sponsored-product/sp-creation/sp-strategy-group-creation-page.component";
import { SpManagerComponent } from "./sponsored-product/sp-manager/sp-manager.component";
import { SpPageComponent } from "./sponsored-product/sp-page/sp-page.component";
import { SpSubStrategyCreationPageComponent } from "./sponsored-product/sp-page/sp-substrategy-creation/sp-substrategy-creation-page.component";
import { SpSubStrategyPageComponent } from "./sponsored-product/sp-page/sp-substrategy-page.component";
import { SpStrategyGroupPageComponent } from "./sponsored-product/sp-page/strategy-group/sp-strategy-group-page.component";
import { ProductGroupPageComponent } from "./strategies/product-group/product-group-page/product-group-page.component";
import { ProductGroupComponent } from "./strategies/product-group/product-group.component";
import { StrategyBoostComponent } from "./strategies/strategy-boost/strategy-boost.component";
import { CreateTacosStrategyComponent } from "./strategies/tacos-strategy/create-tacos-strategy.component";
import { TacosStrategyPageComponent } from "./strategies/tacos-strategy/tacos-strategy-page.component";
import { TacosManagerComponent } from "./tacos/tacos-manager.component";
import { TrafficStatsComponent } from "./traffic-stats/traffic-stats.component";
import { of } from "rxjs";
import { AuthLayoutComponent } from "@front/m19-ui";

export const onlyWhiteLabelSelfServiceGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const layoutService = inject(LayoutSelectorService);
  return layoutService.getBoardType() === BoardType.WHITELABEL_SELFSERVICE;
};

/**
 * Redirect to login page if not authenticated
 *
 */
export const pageGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  return authService.isLoggedIn().pipe(
    switchMap((loggedIn) => {
      if (loggedIn) {
        return authService.user$;
      }
      return of(undefined);
    }),
    map((user) => {
      if (!user) {
        return router.createUrlTree(["login"], { queryParams: { redirect: state.url } });
      }
      if (!user.registered) {
        return router.createUrlTree(["terms-of-use"]);
      }
      return true;
    }),
  );
};

export const onboardingGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  // check if has skipped onboarding
  const onboardingService = inject(OnboardingService);
  const router = inject(Router);
  return onboardingService.currentStep$.pipe(
    map((step) => {
      const redirect = OnBoardingStepRedirects[step];
      if (redirect === true) return true;
      return router.createUrlTree([OnBoardingStepRedirects[step]]);
    }),
  );
};

export const SP_STRATEGIES_URL = "/advertising/sponsored-product";
export const SB_STRATEGIES_URL = "/advertising/sponsored-brands";
export const SD_STRATEGIES_URL = "/advertising/sponsored-display";
export const TACOS_STRATEGIES_URL = "/advertising/tacos-strategies";
export const routes: M19Route[] = [
  {
    path: "",
    redirectTo: "dashboard",
    pathMatch: "full",
  },
  {
    path: "",
    component: AuthLayoutComponent,
    children: [
      {
        path: "login",
        component: LoginComponent,
        title: "Login",
      },
      {
        path: "register",
        component: RegisterComponent,
        title: "Register",
      },
      {
        path: "register-invite",
        component: RegisterInviteComponent,
        title: "Register Invite",
      },
      {
        path: "password-reset",
        component: PasswordResetComponent,
        title: "Reset Password",
      },
      {
        path: "request-password-reset",
        component: RequestPasswordResetComponent,
        title: "Request Password Reset",
      },
      {
        path: "terms-of-use",
        component: TermsOfUseComponent,
        title: "Terms & Conditions",
      },
    ],
  },
  {
    path: "admin-login",
    component: AdminLoginComponent,
    title: "Admin Login",
  },
  {
    path: "products",
    redirectTo: "products/sales-advertising",
    pathMatch: "full",
  },
  {
    path: "strategies",
    redirectTo: "strategies/sponsored-product",
    pathMatch: "full",
  },
  {
    path: "dashboard360",
    component: Dashboard360LayoutComponent,
    data: {
      withAccountSelector: true,
      withDatePicker: true,
      withCurrencySelector: true,
    },
    providers: [
      {
        provide: DASHBOARD_TYPE,
        useValue: "dashboard360",
      },
    ],
    canActivate: [pageGuard],
    resolve: [accountSelectionResolver],
    children: [
      {
        path: "",
        redirectTo: "account360",
        pathMatch: "full",
      },
      {
        path: "account360",
        loadChildren: () => import("./account360/account360.routes").then((r) => r.A360Routes),
        title: "Account 360",
      },
      {
        path: "product360",
        loadChildren: () => import("./product360/product360.routes").then((r) => r.P360Routes),
        title: "Product 360",
      },
      {
        path: "profile",
        component: ProfileComponent,
        title: "Profile",
        data: {
          parentTitle: "page-title.my_profile",
        },
      },
    ],
  },
  {
    path: "",
    component: BoardLayoutComponent,
    canActivate: [pageGuard],
    resolve: [accountSelectionResolver],
    children: [
      {
        path: "",
        canActivate: [onboardingGuard],
        children: [
          {
            path: "dashboard",
            children: [
              {
                path: "home",
                component: AgencyBoardComponent,
                data: {
                  parentTitle: "page-title.home",
                  withDatePicker: true,
                  withCurrencySelector: true,
                  withMultiAccountSelector: true,
                },
              },
              {
                path: "account360",
                component: StatsOverlayWrapperComponent,
                loadChildren: () => import("./account360/account360.routes").then((r) => r.A360Routes),
                data: {
                  parentTitle: "page-title.account_360",
                },
              },
              {
                path: "advertising",
                component: AdvertisingStatsComponent,
                data: {
                  parentTitle: "page-title.advertising",
                  withDatePicker: true,
                  withCurrencySelector: true,
                  withAccountSelector: true,
                },
              },
              {
                path: "profit-and-loss",
                component: ProfitAndLossComponent,
                data: {
                  parentTitle: "page-title.profit_and_loss",
                  withAccountSelector: true,
                },
              },
              {
                path: "account-linking",
                component: AccountLinkingPageComponent,
                title: "Link your Amazon accounts",
                data: {
                  fromOnboarding: false,
                },
              },
              { path: "**", redirectTo: "home" },
            ],
          },
          {
            path: "advertising",
            children: [
              {
                path: "tacos-strategies",
                component: TacosManagerComponent,
                data: {
                  parentTitle: "page-title.tacos",
                  withAccountSelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "tacos-strategies/create",
                component: CreateTacosStrategyComponent,
                data: {
                  parentTitle: "page-title.tacos",
                  parentRouteUrl: TACOS_STRATEGIES_URL,
                  title: "tacos-strategy.create_tacos_strategy",
                  withAccountSelector: true,
                  subPage: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "tacos-strategies/:id",
                component: TacosStrategyPageComponent,
                data: {
                  parentTitle: "page-title.tacos",
                  parentRouteUrl: TACOS_STRATEGIES_URL,
                  withDatePicker: true,
                  withCurrencySelector: true,
                  subPage: true,
                },
              },
              {
                path: "sponsored-product",
                component: SpManagerComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_product",
                  withAccountSelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "sponsored-product/create",
                component: SpStrategyGroupCreationPageComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_product",
                  title: "strategy-group-migration-page.new_strategy_group",
                  parentRouteUrl: SP_STRATEGIES_URL,
                  subPage: true,
                  withAccountSelector: true,
                },
              },
              {
                path: "sponsored-product/strategy-group/:id",
                component: SpStrategyGroupPageComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_product",
                  parentRouteUrl: SP_STRATEGIES_URL,
                  withDatePicker: true,
                  withCurrencySelector: true,
                  subPage: true,
                },
              },
              {
                path: "sponsored-product/strategy-group/:id/create-strategy",
                component: SpSubStrategyCreationPageComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_product",
                  parentRouteUrl: SP_STRATEGIES_URL,
                  subPage: true,
                },
              },
              {
                path: "sponsored-product/strategy-group/:id/:subid",
                component: SpSubStrategyPageComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_product",
                  parentRouteUrl: SP_STRATEGIES_URL,
                  subPage: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                },
              },
              {
                path: "sponsored-product/:id",
                component: SpPageComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_product",
                  parentRouteUrl: SP_STRATEGIES_URL,
                  subPage: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                },
              },
              {
                path: "sponsored-brands",
                component: SbManagerComponent,
                data: {
                  parentTitle: "strategy-page.sponsored_brand",
                  withAccountSelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "sponsored-brands/create",
                component: SbStrategyFormPageComponent,
                data: {
                  parentTitle: "strategy-page.sponsored_brand",
                  parentRouteUrl: SB_STRATEGIES_URL,
                  subPage: true,
                },
              },
              {
                path: "sponsored-brands/:id",
                component: SbPageComponent,
                data: {
                  parentTitle: "strategy-page.sponsored_brand",
                  parentRouteUrl: SB_STRATEGIES_URL,
                  subPage: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                },
              },
              {
                path: "sponsored-display",
                component: SdManagerComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_display",
                  withAccountSelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "sponsored-display/create",
                component: SdStrategyCreationPageComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_display",
                  title: "sd-strategy-creation.new_sponsored_display",
                  parentRouteUrl: SD_STRATEGIES_URL,
                  withAccountSelector: true,
                  subPage: true,
                },
              },
              {
                path: "sponsored-display/:id",
                component: SdPageComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_display",
                  parentRouteUrl: SD_STRATEGIES_URL,
                  subPage: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                },
              },
              {
                path: "hourly-stats",
                component: HourlyPageComponent,
                data: {
                  parentTitle: "page-title.hourly_stats",
                  withAccountSelector: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "promo-days-boost",
                component: StrategyBoostComponent,
                data: {
                  parentTitle: "page-title.promo_days_boost",
                  withAccountSelector: true,
                },
              },
              {
                path: "dsp",
                loadChildren: () => import("./dsp/dsp.routes").then((r) => r.DspRoutes),
              },
              {
                path: "amc",
                component: AmcStatsComponent,
                data: {
                  parentTitle: "page-title.amc",
                  withAccountSelector: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                },
              },
            ],
          },
          {
            path: "product-center",
            children: [
              {
                path: "product360",
                component: StatsOverlayWrapperComponent,
                loadChildren: () => import("./product360/product360.routes").then((r) => r.P360Routes),
                data: {
                  parentTitle: "page-title.product_360",
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "product-analysis",
                component: ProductAnalysisPageComponent,
                data: {
                  parentTitle: "page-title.product_analysis",
                  withAccountSelector: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "product-tracker",
                loadChildren: () => import("./product-tracker/product-tracker.routes").then((r) => r.routes),
                data: {
                  parentTitle: "page-title.product_tracker",
                  parentRouteUrl: "/product-center/product-tracker",
                },
              },
              {
                path: "inventory",
                component: InventoryComponent,
                data: {
                  parentTitle: "page-title.inventory",
                  withAccountSelector: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                },
              },
              {
                path: "catalog",
                component: CatalogPageComponent,
                data: {
                  parentTitle: "page-title.catalog",
                  withAccountSelector: true,
                },
              },
              {
                path: "catalog/upload",
                component: CatalogUploadComponent,
                data: {
                  parentTitle: "page-title.catalog",
                },
              },
              {
                path: "product-group",
                component: ProductGroupComponent,
                data: {
                  parentTitle: "page-title.product_groups",
                  withAccountSelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "product-group/:id",
                component: ProductGroupPageComponent,
                data: {
                  parentTitle: "page-title.product_groups",
                  parentRouteUrl: "/product-center/product-group",
                  subPage: true,
                },
              },
              {
                path: "brand-alias",
                component: BrandAliasComponent,
                data: {
                  parentTitle: "page-title.brand_alias",
                  withAccountSelector: true,
                },
              },
            ],
          },
          {
            path: "keyword-center",
            children: [
              {
                path: "traffic-analysis",
                component: TrafficStatsComponent,
                data: {
                  parentTitle: "page-title.traffic_analysis",
                  withAccountSelector: true,
                  withDatePicker: true,
                  withCurrencySelector: true,
                },
              },
              {
                path: "segments",
                redirectTo: "segments/keyword-segments",
                pathMatch: "full",
                data: {
                  parentTitle: "page-title.segments",
                },
              },
              {
                path: "segments/keyword-segments",
                component: SegmentsComponent,
                data: {
                  parentTitle: "page-title.segments",
                  withAccountSelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "segments/product-segments",
                component: SegmentsComponent,
                data: {
                  parentTitle: "page-title.segments",
                  withAccountSelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "share-of-voice",
                component: ShareOfVoiceComponent,
                data: {
                  parentTitle: "page-title.share_of_voice",
                  withDatePicker: true,
                  withAccountSelector: true,
                },
              },
              {
                path: "keyword-tracker",
                component: KeywordTrackerPageComponent,
                data: {
                  parentTitle: "page-title.keyword_tracker",
                  withDatePicker: true,
                  withAccountSelector: true,
                  withChatbotSafeMargin: true,
                },
              },
              {
                path: "keyword-tracker/:searchterm",
                component: KeywordTrackerDetailsComponent,
                data: {
                  parentTitle: "page-title.keyword_tracker",
                  parentRouteUrl: "/keyword-center/keyword-tracker",
                  withDatePicker: true,
                  subPage: true,
                },
              },
              {
                path: "search-trends",
                component: SearchTermRanksComponent,
                data: {
                  parentTitle: "page-title.search_trends",
                  withDatePicker: true,
                  withAccountSelector: true,
                },
              },
            ],
          },
          {
            path: "activities",
            component: ActivitiesComponent,
            data: {
              parentTitle: "page-title.activities",
              withDatePicker: true,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: "profile",
            component: ProfileComponent,
            data: {
              parentTitle: "page-title.my_profile",
            },
          },
          {
            path: "accounts",
            component: AccountSettingComponent,
            data: {
              parentTitle: "page-title.accounts",
              withChatbotSafeMargin: true,
            },
          },
          {
            path: "user-management",
            component: UserManagementComponent,
            data: {
              parentTitle: "page-title.user_management",
              withDatePicker: true,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: "billing",
            component: BillingSettingsComponent,
            data: {
              parentTitle: "page-title.billing",
            },
          },
          {
            path: "freemium",
            component: SelfServiceFreemiumComponent,
            canActivate: [onlyWhiteLabelSelfServiceGuard],
            data: {
              parentTitle: "page-title.freemium",
            },
          },
          {
            path: "notifications-center",
            component: NotificationsCenterComponent,
            data: {
              parentTitle: "page-title.notifications",
            },
          },
          {
            path: "datahub",
            component: DataSharingComponent,
            data: {
              parentTitle: "page-title.data_hub",
              withChatbotSafeMargin: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: "onboarding",
    component: OnboardingLayoutComponent,
    canActivate: [pageGuard],
    children: [
      {
        path: "account-linking",
        component: AccountLinkingPageComponent,
        title: "Link your Amazon accounts",
        data: {
          fromOnboarding: true,
        },
      },
      {
        path: "downloading-stats",
        component: StatsDownloadingPageComponent,
        title: "Downloading Stats",
      },
      {
        path: "onboarding-call",
        component: OnboardingCallPageComponent,
        title: "Free Onboarding Call",
      },
      {
        path: "free-test-subscription",
        component: FreeTestPageComponent,
        title: "Free Test Subscription",
      },
      {
        path: "agency-no-account",
        component: WhitelabelNoAccountPageComponent,
        title: "No Accounts",
      },
    ],
  },
  { path: "**", redirectTo: "dashboard" },
];

@Injectable()
export class TemplatePageTitleStrategy extends TitleStrategy {
  constructor(
    private readonly title: Title,
    private readonly layoutSelectorService: LayoutSelectorService,
  ) {
    super();
  }

  override updateTitle(routerState: RouterStateSnapshot) {
    const title = this.buildTitle(routerState);
    const pageTitle = this.layoutSelectorService.getPageTitle();
    if (title !== undefined) {
      this.title.setTitle(`${pageTitle} - ${title}`);
    } else {
      this.title.setTitle(pageTitle);
    }
  }
}
